import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { HardHat, Loader2 } from "lucide-react";
import { OnboardingStep1 } from "@/components/onboarding/OnboardingStep1";
import { OnboardingStep2 } from "@/components/onboarding/OnboardingStep2";
import { Card, CardContent } from "@/components/ui/card";
import { apiFetch } from "@/lib/api";
import { onboardingKeys } from "@/lib/query";

interface OnboardingState {
  onboardingStatus: string;
  onboardingStep: number;
  userType: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone: string | null;
  jobTitle: string | null;
  profileImageUrl: string | null;
}

export function OnboardingPage() {
  const router = useRouter();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: onboardingKeys.status(),
    queryFn: () => apiFetch<OnboardingState>("/onboarding"),
  });

  if (isLoading || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (data.onboardingStatus === "complete") {
    router.navigate({ to: "/" });
    return null;
  }

  async function handleStepComplete() {
    await qc.invalidateQueries({ queryKey: onboardingKeys.status() });
    const fresh = await apiFetch<OnboardingState>("/onboarding");
    if (fresh.onboardingStatus === "complete") {
      router.navigate({ to: "/" });
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-slate-950 px-4">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/login-bg.jpg')" }}
      />
      <div className="absolute inset-0 bg-slate-950/80" />

      <div className="relative z-10 w-full max-w-lg">
        <div className="mb-8 flex flex-col items-center gap-4">
          <img
            src="/logo-white.png"
            alt="Prominent Builders"
            className="h-auto w-64 drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]"
          />

          <div className="flex items-center gap-2">
            {[1, 2].map((step) => (
              <div key={step} className="flex items-center gap-2">
                <div
                  className={`flex size-7 items-center justify-center rounded-full text-xs font-semibold ${
                    data.onboardingStep >= step
                      ? "bg-[#415A77] text-white"
                      : "bg-slate-700 text-slate-400"
                  }`}
                >
                  {step}
                </div>
                {step < 2 && <div className="h-px w-8 bg-slate-600" />}
              </div>
            ))}
          </div>
        </div>

        <Card className="border-slate-700/50 bg-slate-800/90 shadow-2xl backdrop-blur">
          <CardContent className="pt-6">
            {data.onboardingStep === 1 ? (
              <OnboardingStep1 firstName={data.firstName ?? ""} onComplete={handleStepComplete} />
            ) : (
              <OnboardingStep2
                jobTitle={data.jobTitle}
                phone={data.phone}
                onComplete={handleStepComplete}
              />
            )}
          </CardContent>
        </Card>

        <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-500">
          <HardHat className="size-3.5" />
          <span>Prominent Builders OS</span>
        </div>
      </div>
    </div>
  );
}
